import React, { useMemo, useState } from "react";
import {
  FiDownload,
  FiCalendar,
  FiFileText,
  FiSearch,
  FiClock,
  FiBookOpen,
  FiArchive,
  FiArrowDownCircle,
  FiChevronRight,
  FiX,
} from "react-icons/fi";

const theme = {
  primary: "#063b87",
  secondary: "#0a57bb",
  success: "#087344",
  gradient: "linear-gradient(135deg, #063b87 0%, #0a57bb 55%, #087344 100%)",
  dark: "#15345b",
  light: "#f2f6fb",
  accent: "#0a57bb",
  border: "#dce5f0",
  text: "#34465e",
  muted: "#738196",
};

const formatDate = (date) => {
  if (!date) return "Date unavailable";

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;

  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const formatMonth = (date) => {
  if (!date) return "";
  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

// Reusable download button
const DownloadButton = ({
  diary,
  onDownload,
  downloadProgress,
  compact = false,
  downLoading,
}) => {
  const className = compact
    ? "btn btn-sm d-inline-flex align-items-center gap-2"
    : "btn d-inline-flex align-items-center justify-content-center gap-2";

  const style = {
    background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
    color: "#fff",
    border: "none",
    borderRadius: "9px",
    padding: compact ? "7px 13px" : "11px 20px",
    fontWeight: 600,
    fontSize: compact ? "12px" : "14px",
    boxShadow: "0 3px 9px rgba(95, 54, 43, 0.16)",
    whiteSpace: "nowrap",
  };

  if (onDownload) {
    return (
      <>
        {downLoading ? (
          <div
            className="p-3 rounded-3 bg-light border "
            style={{ fontSize: "12px" }}
          >
            <div className="d-flex justify-content-between mb-2">
              <strong>Downloading DRM Diary...</strong>
              <strong>{downloadProgress}%</strong>
            </div>

            <div
              className="progress"
              style={{ height: "10px", borderRadius: "10px" }}
            >
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                style={{
                  width: `${downloadProgress}%`,
                  background: "linear-gradient(135deg, #4587b3, #157308)",
                }}
                aria-valuenow={downloadProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              />
            </div>
          </div>
        ) : (
          <button
            type="button"
            className={className}
            style={style}
            onClick={() => onDownload(diary)}
            disabled={downLoading}
          >
            <FiDownload size={15} />
            Download PDF
          </button>
        )}
      </>
    );
  }

  if (!diary?.downloadUrl) {
    return (
      <button
        type="button"
        className={className}
        style={{ ...style, opacity: 0.5 }}
        disabled
      >
        <FiDownload size={15} />
        Unavailable
      </button>
    );
  }

  return (
    <a
      href={diary.downloadUrl}
      download={diary.fileName || true}
      className={className}
      style={{ ...style, textDecoration: "none" }}
      target="_blank"
      rel="noopener noreferrer"
    >
      <FiDownload size={15} />
      Download PDF
    </a>
  );
};

// Diary thumbnail
const DiaryThumbnail = ({ diary, large = false }) => {
  return (
    <div
      className="position-relative overflow-hidden d-flex align-items-center justify-content-center  "
      style={{
        width: "100%",
        height: large ? "100%" : "105px",
        borderRadius: large ? "12px" : "9px",
        background: theme.gradient,
      }}
    >
      {diary?.thumbnail_url ? (
        <img
          src={diary.thumbnail_url}
          alt={`${diary.title} thumbnail`}
          className="w-100 h-100"
          style={{ objectFit: "cover" }}
        />
      ) : (
        <div
          className="text-center"
          style={{
            color: "#fff",
            padding: "12px",
          }}
        >
          <FiBookOpen size={large ? 49 : 34} />
          <div
            className="mt-2 fw-bold"
            style={{
              fontSize: large ? "15px" : "11px",
              letterSpacing: "1.5px",
              color: "#fff",
            }}
          >
            DRM DIARY
          </div>
          <div style={{ fontSize: "10px", opacity: 0.85 }}>
            {diary?.year || ""}
          </div>
        </div>
      )}

      <span
        className="position-absolute bottom-0 end-0 m-2 px-2 py-1 rounded"
        style={{
          background: "rgba(255,255,255,0.92)",
          color: theme.primary,
          fontSize: "10px",
          fontWeight: 700,
        }}
      >
        PDF
      </span>
    </div>
  );
};

const DRMDiary = ({
  currentDiary = null,
  diaries = [],
  onDownload,
  downloadProgress,
  downLoading,
  title = "DRM Diary",
  subtitle = "Digital diary and document archive",
}) => {
  const [search, setSearch] = useState("");
  const [selectedYear, setSelectedYear] = useState("All");
  const [showAll, setShowAll] = useState(false);

  const history = useMemo(() => {
    return [...diaries].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [diaries]);

  const years = useMemo(() => {
    return [
      "All",
      ...new Set(
        history
          .map((d) => d.year || new Date(d.date).getFullYear())
          .filter(Boolean)
          .map(String),
      ),
    ];
  }, [history]);

  const filteredDiaries = useMemo(() => {
    return history.filter((diary) => {
      const query = search.toLowerCase();

      const matchesSearch =
        diary.title?.toLowerCase().includes(query) ||
        diary.description?.toLowerCase().includes(query) ||
        diary.month?.toLowerCase().includes(query) ||
        String(diary.year || "").includes(query);

      const diaryYear = String(
        diary.year || new Date(diary.date).getFullYear(),
      );

      const matchesYear = selectedYear === "All" || diaryYear === selectedYear;

      return matchesSearch && matchesYear;
    });
  }, [history, search, selectedYear]);

  const visibleDiaries = showAll
    ? filteredDiaries
    : filteredDiaries.slice(0, 6);

  return (
    <div
      className="container-fluid py-3"
      style={{
        color: theme.text,
        background: "#faf8f6",
        minHeight: "100vh",
      }}
    >
      {/* HEADER */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: 43,
                height: 43,
                borderRadius: "12px",
                color: "#fff",
                background: `linear-gradient(135deg, ${theme.primary}, ${theme.secondary})`,
              }}
            >
              <FiBookOpen size={23} />
            </div>

            <div>
              <h4 className="fw-bold mb-0" style={{ color: theme.dark }}>
                {title}
              </h4>
              <small style={{ color: theme.muted }}>{subtitle}</small>
            </div>
          </div>
        </div>

        <div
          className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill"
          style={{
            background: "#f0e7df",
            color: theme.primary,
            fontSize: "12px",
            fontWeight: 600,
          }}
        >
          <FiArchive size={15} />
          {history.length} Previous Diaries
        </div>
      </div>

      {/* CURRENT DIARY */}
      {currentDiary && (
        <div className="mb-4">
          <div
            className="d-flex align-items-center gap-2 mb-3"
            style={{ color: theme.primary }}
          >
            <FiArrowDownCircle size={18} />
            <span className="fw-bold small text-uppercase">
              Latest Available Diary
            </span>
          </div>

          <div
            className=" m-2  overflow-hidden d-flex justify-content-center align-items-center"
            style={{}}
          >
            <div
              className="m-2 d-flex align-items-stretch mx-auto w-100"
              style={{
                borderRadius: "16px",
                boxShadow: "0 5px 22px rgba(6, 59, 135, 0.08)",
                background: "#fff",
                maxWidth: "900px",
                overflow: "hidden",
              }}
            >
              {/* LEFT THUMBNAIL */}
              <div className="">
                <DiaryThumbnail diary={currentDiary} large />
              </div>

              {/* RIGHT CONTENT */}
              <div className="flex-grow-1 d-flex flex-column min-w-0">
                <div className="p-3 p-md-4 d-flex flex-column flex-grow-1 align-items-between justify-content-center ">
                  {/* TOP LEFT */}
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                    <span
                      className="badge rounded-pill px-3 py-2"
                      style={{
                        background: "#e8f0fa",
                        color: theme.primary,
                      }}
                    >
                      Latest Diary
                    </span>

                    <span className="small" style={{ color: theme.muted }}>
                      <FiFileText className="me-1" />
                      PDF Document
                    </span>
                  </div>

                  {/* MIDDLE CONTENT */}
                  <div className="flex-grow-1  ">
                    <h4 className="fw-bold mb-2" style={{ color: theme.dark }}>
                      {currentDiary.title}
                    </h4>

                    <p
                      className="mb-3"
                      style={{
                        color: theme.muted,
                        fontSize: "13px",
                        maxWidth: "650px",
                        lineHeight: 1.7,
                      }}
                    >
                      {currentDiary.description ||
                        "Access and download the latest DRM diary document."}
                    </p>

                    <div className="d-flex flex-wrap gap-3">
                      <span className="small d-flex align-items-center gap-2">
                        <FiCalendar color={theme.secondary} />
                        {formatDate(currentDiary.date)}
                      </span>

                      {currentDiary.fileSize && (
                        <span className="small d-flex align-items-center gap-2">
                          <FiFileText color={theme.secondary} />
                          {currentDiary.fileSize}
                        </span>
                      )}

                      {currentDiary.period && (
                        <span className="small d-flex align-items-center gap-2">
                          <FiClock color={theme.secondary} />
                          {currentDiary.period}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* BOTTOM RIGHT */}
                  <div className="d-flex justify-content-end align-items-center mt-4 pt-2">
                    <DownloadButton
                      downLoading={downLoading}
                      diary={currentDiary}
                      downloadProgress={downloadProgress}
                      onDownload={onDownload}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <hr />
      {/* HISTORY HEADER */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
        <div>
          <h5 className="fw-bold mb-1" style={{ color: theme.dark }}>
            <FiArchive className="me-2" color={theme.secondary} />
            Diary History
          </h5>
          <small style={{ color: theme.muted }}>
            Browse and download previously published diaries
          </small>
        </div>

        <div className="d-flex flex-wrap gap-2">
          {/* SEARCH */}
          <div
            className="d-flex align-items-center px-3"
            style={{
              background: "#fff",
              border: `1px solid ${theme.border}`,
              borderRadius: "9px",
              minWidth: "190px",
            }}
          >
            <FiSearch color={theme.muted} />
            <input
              type="text"
              className="form-control border-0 shadow-none"
              placeholder="Search diaries..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ fontSize: "13px" }}
            />
            {search && (
              <button
                className="btn p-0 border-0"
                onClick={() => setSearch("")}
                type="button"
              >
                <FiX color={theme.muted} />
              </button>
            )}
          </div>

          {/* YEAR FILTER */}
          <select
            className="form-select shadow-none"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            style={{
              width: "145px",
              borderRadius: "9px",
              borderColor: theme.border,
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year === "All" ? "All Years" : year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* HISTORY CARDS */}
      {visibleDiaries.length > 0 ? (
        <>
          <div className="row g-3">
            {visibleDiaries.map((diary, index) => (
              <div
                className="col-12 col-sm-6 col-lg-4 col-xl-3"
                key={diary.id || index}
              >
                <div
                  className="card h-100 border-0 d-flex"
                  style={{
                    borderRadius: "13px",
                    background: "#fff",
                    boxShadow: "0 3px 13px rgba(73, 45, 37, 0.06)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 22px rgba(73, 45, 37, 0.12)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 3px 13px rgba(73, 45, 37, 0.06)";
                  }}
                >
                  <div className="p-2">
                    <DiaryThumbnail diary={diary} large={true} />
                  </div>

                  <div className="card-body pt-2 d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span
                        className="small fw-semibold"
                        style={{ color: theme.secondary }}
                      >
                        {diary.month || formatMonth(diary.date)}
                      </span>

                      <span
                        className="badge rounded-pill"
                        style={{
                          background: "#f5eee8",
                          color: theme.primary,
                          fontSize: "10px",
                        }}
                      >
                        {diary.year || ""}
                      </span>
                    </div>

                    <h6 className="fw-bold mb-2" style={{ color: theme.dark }}>
                      {diary.title}
                    </h6>

                    <p
                      className="small mb-2"
                      style={{
                        color: theme.muted,
                        fontSize: "12px",
                        minHeight: "35px",
                      }}
                    >
                      {diary.description.slice(0, 100) || "DRM Diary document"}
                    </p>

                    <div
                      className="d-flex align-items-center gap-2 small mb-3"
                      style={{ color: theme.muted, fontSize: "11px" }}
                    >
                      <FiCalendar size={13} />
                      {formatDate(diary.date)}
                      {diary.fileSize && (
                        <>
                          <span>•</span>
                          {diary.fileSize}
                        </>
                      )}
                    </div>

                    <div className="mt-auto pt-2 border-top d-flex justify-content-between align-items-center">
                      <span
                        className="small fw-semibold"
                        style={{ color: theme.secondary }}
                      >
                        <FiFileText className="me-1" />
                        PDF
                      </span>

                      <DownloadButton
                        diary={diary}
                        downLoading={downLoading}
                        downloadProgress={downloadProgress}
                        onDownload={onDownload}
                        compact
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* VIEW MORE */}
          {filteredDiaries.length > 6 && (
            <div className="text-center mt-4">
              <button
                className="btn px-4 py-2"
                onClick={() => setShowAll(!showAll)}
                style={{
                  color: theme.primary,
                  background: "#f0e7df",
                  borderRadius: "9px",
                  fontWeight: 600,
                  fontSize: "13px",
                }}
              >
                {showAll ? "Show Less" : "View All Diaries"}
                <FiChevronRight className="ms-2" />
              </button>
            </div>
          )}
        </>
      ) : (
        <div
          className="text-center py-5 rounded-4"
          style={{
            background: "#fff",
            border: `1px dashed ${theme.border}`,
          }}
        >
          <FiArchive size={42} color="#c5a995" />
          <h6 className="fw-bold mt-3" style={{ color: theme.dark }}>
            No Diaries Found
          </h6>
          <p className="small mb-0" style={{ color: theme.muted }}>
            Try changing your search or selected year.
          </p>
        </div>
      )}
    </div>
  );
};

export default DRMDiary;
